import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Horizontaler Boxplot aus der Fuenf-Punkte-Zusammenfassung (#252).
 *
 * Kasten von Q1 bis Q3, Strich am Median, Whisker an den **Tukey-Zaeunen**
 * (Q1 - 1,5 x IQR bzw. Q3 + 1,5 x IQR), min/max als Punkte dahinter.
 *
 * **Der Ausreisser-Hinweis ist derselbe Baustein.** Liegt ein Extremwert
 * ausserhalb des Zauns, ist das im Bild zu sehen und wird zusaetzlich benannt.
 * Gerechnet wird er aus Feldern, die das Bundle schon fuehrt -- keine neue
 * Abfrage, keine Contract-Fassung.
 *
 * An `Heart Rate` (chartevents.itemid=220045) gemessen: Q1 77, Q3 101, IQR 24,
 * Zaun 41 bis 137, min 0 und max 157 -- **beide Enden ausserhalb**. 61 von 8094
 * Werten liegen draussen, fuenf davon sind exakt 0.
 *
 * Kein Urteil ueber die Werte: ein Ausreisser ist keine Fehlmessung. Eine
 * Herzfrequenz von 157 ist plausibel, eine von 0 kann Asystolie sein. Diese
 * Anzeige beschreibt, sie filtert nicht -- Filtern ist #125.
 */
const BoxplotZeile = ({ muster }) => {
  const { t } = useTranslation();

  const zahl = (w) => (Number.isFinite(Number(w)) && w !== null ? Number(w) : null);
  const min = zahl(muster?.minValue);
  const max = zahl(muster?.maxValue);
  const q1 = zahl(muster?.q1Value);
  const q3 = zahl(muster?.q3Value);
  const median = zahl(muster?.medianValue);

  // Ohne die fuenf Punkte gibt es keinen Boxplot. Ein halber waere eine
  // Andeutung, keine Aussage.
  if ([min, max, q1, q3, median].some((w) => w === null) || max <= min) {
    return null;
  }

  const iqr = q3 - q1;
  const zaunUnten = q1 - 1.5 * iqr;
  const zaunOben = q3 + 1.5 * iqr;
  // Die Whisker enden am letzten Wert **innerhalb** des Zauns -- so ist es
  // konventionell, und so wird der Abstand zu min/max sichtbar. Die genauen
  // Werte dort kennen wir nicht, also nehmen wir den Zaun, begrenzt auf die
  // tatsaechliche Spannweite.
  const whiskerUnten = Math.max(min, zaunUnten);
  const whiskerOben = Math.min(max, zaunOben);

  const ausreisserUnten = min < zaunUnten;
  const ausreisserOben = max > zaunOben;

  const spanne = max - min;
  const pos = (w) => ((w - min) / spanne) * 100;
  const kurz = (w) => (Number.isInteger(w) ? String(w) : w.toFixed(2).replace(/0$/, ''));

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ position: 'relative', height: 30, mx: 1 }}>
        {/* Whisker-Linie */}
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: `${pos(whiskerUnten)}%`,
            width: `${pos(whiskerOben) - pos(whiskerUnten)}%`,
            height: 2,
            backgroundColor: 'grey.500',
          }}
        />
        {/* Kasten Q1 bis Q3 */}
        <Tooltip title={`Q1 ${kurz(q1)} – Q3 ${kurz(q3)} (IQR ${kurz(iqr)})`}>
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: `${pos(q1)}%`,
              width: `${Math.max(pos(q3) - pos(q1), 0.5)}%`,
              height: 18,
              backgroundColor: 'primary.light',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 0.5,
            }}
          />
        </Tooltip>
        {/* Median */}
        <Tooltip title={`${t('dataCatalog.expanded.median')} ${kurz(median)}`}>
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              left: `${pos(median)}%`,
              width: 2,
              height: 22,
              backgroundColor: 'primary.dark',
            }}
          />
        </Tooltip>
        {/* min und max als Punkte -- ausserhalb des Zauns eingefaerbt */}
        {[
          { wert: min, links: 0, aus: ausreisserUnten },
          { wert: max, links: 100, aus: ausreisserOben },
        ].map((punkt) => (
          <Tooltip key={punkt.links} title={`${kurz(punkt.wert)}${punkt.aus ? ' — ' + t('dataCatalog.expanded.outlierPoint') : ''}`}>
            <Box
              sx={{
                position: 'absolute',
                top: 11,
                left: `calc(${punkt.links}% - 4px)`,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: punkt.aus ? 'warning.main' : 'grey.500',
              }}
            />
          </Tooltip>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mx: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {kurz(min)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {kurz(max)}
        </Typography>
      </Box>

      {(ausreisserUnten || ausreisserOben) && (
        <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mx: 1, mt: 0.5 }}>
          {t('dataCatalog.expanded.outlierHint', {
            unten: kurz(zaunUnten),
            oben: kurz(zaunOben),
          })}
        </Typography>
      )}
    </Box>
  );
};

export default BoxplotZeile;
